import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";

const CustomerTable = ({
  customers,
  onEdit,
  onDelete,
  onView,
  searchTerm,
  onSearchChange,
  sortConfig,
  onSort,
  selectedCustomers,
  onSelectCustomer,
  onSelectAll,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const sortedCustomers = useMemo(() => {
    if (!sortConfig.key) return customers;

    return [...customers].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [customers, sortConfig]);

  const filteredCustomers = useMemo(() => {
    return sortedCustomers.filter(
      (customer) =>
        `${customer.first_name} ${customer.last_name}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.includes(searchTerm)
    );
  }, [sortedCustomers, searchTerm]);

  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredCustomers.slice(startIndex, startIndex + pageSize);
  }, [filteredCustomers, currentPage, pageSize]);

  const totalPages = 50;

  const handleSort = (key) => {
    onSort(key);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: "bg-success text-success-foreground",
        label: t("active"),
      },
      inactive: {
        color: "bg-secondary text-secondary-foreground",
        label: t("inactive"),
      },
      pending: {
        color: "bg-warning text-warning-foreground",
        label: t("pending"),
      },
      blocked: { color: "bg-error text-error-foreground", label: t("blocked") },
    };

    const config = statusConfig[status] || statusConfig.active;
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const allSelected =
    selectedCustomers.length === paginatedCustomers.length &&
    paginatedCustomers.length > 0;
  const someSelected =
    selectedCustomers.length > 0 &&
    selectedCustomers.length < paginatedCustomers.length;

  return (
    <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200">
      {/* Table Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            {t("customerRecords")}
          </h3>
          <div className="flex items-center space-x-3">
            <Input
              type="search"
              placeholder={t("searchCustomers")}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-64"
            />
         
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm text-gray-700">
        <thead className="bg-green-50 text-green-900 font-semibold">
            <tr>
              {[
                { key: "name", label: t("customerName") },
                { key: "email", label: t("email") },
                { key: "phone", label: t("phone") },
                { key: "order_count", label: t("orderNumber") },
                { key: "status", label: t("status") },
                { key: "lastContact", label: t("lastContact") },
              ].map((column) => (
                <th
                  key={column.key}
                  className="px-4 py-3 text-left text-sm font-medium text-foreground cursor-pointer hover:bg-muted transition-micro"
                  onClick={() => handleSort(column.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {sortConfig.key === column.key && (
                      <Icon
                        name={
                          sortConfig.direction === "asc"
                            ? "ChevronUp"
                            : "ChevronDown"
                        }
                        size={16}
                        className="text-primary"
                      />
                    )}
                  </div>
                </th>
              ))}
              <th className="w-24 px-4 py-3 text-center text-sm font-medium text-foreground">
                {t("actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedCustomers.map((customer) => (
              <tr
                key={customer.id}
                className="hover:bg-surface transition-micro cursor-pointer"
                onClick={() => onView(customer)}
              >
      
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div>
                      <div className="font-medium text-foreground">
                        {customer.first_name} {customer.last_name}
                      </div>
               
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  {customer.email}
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  {customer.phone}
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  {customer.order_count}
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(customer.role.name)}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {formatDate(customer.lastContact || new Date())}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-center space-x-1">
                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onView(customer)}
                      className="w-8 h-8"
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(customer)}
                      className="w-8 h-8"
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(customer)}
                      className="w-8 h-8 text-error hover:text-error"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-border">
        {paginatedCustomers.map((customer) => (
          <div key={customer.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div>
                  <div className="font-medium text-foreground">
                    {customer.first_name} {customer.last_name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {customer.company || "N/A"}
                  </div>
                </div>
              </div>
              {getStatusBadge(customer.role.name)}
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Mail" size={14} className="text-muted-foreground" />
                <span className="text-foreground">{customer.email}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon
                  name="Phone"
                  size={14}
                  className="text-muted-foreground"
                />
                <span className="text-foreground">{customer.phone}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon
                  name="Calendar"
                  size={14}
                  className="text-muted-foreground"
                />
                <span className="text-muted-foreground">
                  {t("lastContact")}:{" "}
                  {formatDate(customer.lastContact || new Date())}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-2">
              <button
                variant="outline"
                size="sm"
                onClick={() => onView(customer)}
              >
                <Icon name="Eye" size={16} className="mr-2" />
                {t("view")}
              </button>
              <button
                variant="outline"
                size="sm"
                onClick={() => onEdit(customer)}
              >
                <Icon name="Edit" size={16} className="mr-2" />
                {t("edit")}
              </button>
              <button
                variant="outline"
                size="sm"
                onClick={() => onDelete(customer)}
                className="text-error hover:text-error"
              >
                <Icon name="Trash2" size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerTable;
